import { FileSignature, Send, Loader2, DollarSign } from "lucide-react";

// Quick-offer + contract-send action grid shown under the phones column
// on the LEAD INFO tab. Owner ask 2026-08-28: three tiers of quick
// offer visible at all times so the rep can jump between price points
// mid-call without opening a modal, and three matching contract-send
// buttons to fire the SignWell PDF for whichever tier the seller likes.
//
// Layout: 3-column grid, 2 rows. Row 1 = "Quick Offer" preview buttons
// (open the offer popup pre-selected to that tier). Row 2 = "Send
// Contract" (fire SignWell for that tier without opening the popup).
//
// Props:
//   tiers            — the /api/dialer/offer-tiers response (list of {key, label, price, ...})
//   sending          — bool while the SignWell POST is in flight
//   onPreviewTier    — (tierKey) => void  (opens the offer popup at that tier)
//   onSendTier       — (tierKey) => void  (fires sendOfferNow at that tier)
//   canSend          — bool (are propInfo + sellerEmail ready?)
export default function QuickOfferButtons({
  tiers = [],
  sending = false,
  onPreviewTier,
  onSendTier,
  canSend = false,
}) {
  // Map the tenant's tiers into 3 slots (fallbacks kept so the UI never
  // renders empty when property enrichment is still loading).
  const slots = [
    tiers[0] || { key: "quick",       label: "Quick",       price: 0 },
    tiers[1] || { key: "recommended", label: "Recommended", price: 0 },
    tiers[2] || { key: "max_safe",    label: "Final",       price: 0 },
  ];

  const fmt = (n) => (n && n > 0) ? `$${Math.round(n).toLocaleString()}` : "—";

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-2.5"
         data-testid="dialer-quick-offer-grid">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
        <DollarSign size={11} className="text-emerald-600" />
        Mid-call offer ladder
      </div>

      {/* Row 1 — Quick Offer preview buttons */}
      <div className="grid grid-cols-3 gap-2">
        {slots.map((t, i) => (
          <button
            key={`preview-${t.key}-${i}`}
            onClick={() => onPreviewTier && onPreviewTier(t.key)}
            disabled={!t.price}
            data-testid={`qo-preview-${i + 1}`}
            className={`rounded-lg border-2 px-2 py-2 transition text-left ${
              t.price
                ? "border-emerald-300 bg-white hover:border-emerald-500 hover:bg-emerald-50"
                : "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
            }`}
          >
            <div className="text-[9px] uppercase tracking-widest font-bold text-emerald-700">
              {i === 0 ? "1 · Quick Offer" : i === 1 ? "2 · Quick Offer" : "3 · Final Offer"}
            </div>
            <div className="text-sm font-bold text-slate-800 mt-0.5 tabular-nums">
              {fmt(t.price)}
            </div>
            <div className="text-[9px] text-slate-500 truncate">{t.label || "—"}</div>
          </button>
        ))}
      </div>

      {/* Row 2 — Send Contract buttons */}
      <div className="grid grid-cols-3 gap-2 mt-2">
        {slots.map((t, i) => (
          <button
            key={`send-${t.key}-${i}`}
            onClick={() => onSendTier && onSendTier(t.key)}
            disabled={!canSend || !t.price || sending}
            data-testid={`qo-send-${i + 1}`}
            className={`rounded-lg px-2 py-2 transition inline-flex items-center justify-center gap-1 text-[11px] font-semibold ${
              canSend && t.price && !sending
                ? "bg-slate-800 text-white hover:bg-slate-900"
                : "bg-slate-200 text-slate-400 cursor-not-allowed"
            }`}
          >
            {sending
              ? <Loader2 size={11} className="animate-spin" />
              : <FileSignature size={11} />}
            <span className="truncate">
              {i === 2 ? "Send Contract" : "Send Contract"}
            </span>
          </button>
        ))}
      </div>

      {!canSend && (
        <div className="text-[10px] text-slate-400 mt-2 text-center italic">
          Run a Property Lookup + set the seller email to enable sending
        </div>
      )}
    </div>
  );
}
